import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  useUpdateClientUserMutation,
  type Employee
} from "@/api/clientApi";
import { useRequestPasswordResetMutation } from "@/api/authApi";
import { toast } from "sonner";

interface EditClientUserProps {
  clientId: number;
  employee: Employee;
  onSuccess?: () => void;
}

export default function EditClientUser({
  clientId,
  employee,
  onSuccess,
}: EditClientUserProps) {
  const [form, setForm] = useState({
    firstName: employee.firstName || "",
    lastName: employee.lastName || "",
    email: employee.email || "",
    phoneNumber: employee.phoneNumber || employee.contactPhone || "",
  });

  const [showPasswordReset, setShowPasswordReset] = useState(false);

  const [updateUser, { isLoading: isUpdating }] = useUpdateClientUserMutation();
  const [requestReset, { isLoading: isResetting }] = useRequestPasswordResetMutation();

  useEffect(() => {
    setForm({
      firstName: employee.firstName || "",
      lastName: employee.lastName || "",
      email: employee.email || "",
      phoneNumber: employee.phoneNumber || employee.contactPhone || "",
    });
  }, [employee]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!form.firstName || !form.lastName || !form.email) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      await updateUser({
        clientId,
        userId: employee.id,
        ...form
      }).unwrap();

      toast.success("Employee updated successfully");
      if (onSuccess) {
        onSuccess();
      }
    } catch (err: any) {
      toast.error(err?.data?.message || "Failed to update employee");
    }
  };

  const handleResetPassword = async () => {
    const savedEmail = employee.email?.trim();
    const formEmail = form.email.trim();

    if (!savedEmail) {
      toast.error("This employee does not have a saved email address yet");
      return;
    }

    if (formEmail !== savedEmail) {
      toast.error("Please save the updated email before sending a reset link");
      return;
    }

    try {
      await requestReset({ email: savedEmail }).unwrap();
      toast.success("Password reset email sent successfully");
      setShowPasswordReset(false);
    } catch (err: any) {
      toast.error(err?.data?.message || "Failed to send reset email");
    }
  };

  return (
    <div className="space-y-6">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="firstName">First Name *</Label>
            <Input
              id="firstName"
              placeholder="First Name"
              value={form.firstName}
              onChange={(e) => setForm({ ...form, firstName: e.target.value })}
              required
              className="mt-1.5"
            />
          </div>
          <div>
            <Label htmlFor="lastName">Last Name *</Label>
            <Input
              id="lastName"
              placeholder="Last Name"
              value={form.lastName}
              onChange={(e) => setForm({ ...form, lastName: e.target.value })}
              required
              className="mt-1.5"
            />
          </div>
        </div>

        <div>
          <Label htmlFor="email">Email *</Label>
          <Input
            id="email"
            type="email"
            placeholder="employee@example.com"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            required
            className="mt-1.5"
          />
        </div>

        <div>
          <Label htmlFor="phoneNumber">Phone Number</Label>
          <Input
            id="phoneNumber"
            placeholder="+1234567890"
            value={form.phoneNumber}
            onChange={(e) => setForm({ ...form, phoneNumber: e.target.value })}
            className="mt-1.5"
          />
        </div>

        <Button type="submit" className="w-full" disabled={isUpdating}>
          {isUpdating ? "Updating..." : "Update Employee Details"}
        </Button>
      </form>

      <div className="border-t pt-6">
        {!showPasswordReset ? (
          <Button
            variant="outline"
            className="w-full border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100"
            onClick={() => setShowPasswordReset(true)}
          >
            Reset Password
          </Button>
        ) : (
          <div className="space-y-4 rounded-lg bg-slate-50 p-4 border border-slate-200">
            <h4 className="text-sm font-bold text-slate-900">Reset Password</h4>
            <p className="text-xs text-slate-600">
              This will send an email to <strong>{employee.email}</strong> with a link to choose a new password. The link will be valid for 10 minutes.
            </p>
            <div className="flex gap-2">
              <Button
                variant="destructive"
                onClick={handleResetPassword}
                disabled={isResetting}
                className="flex-1"
              >
                {isResetting ? "Sending..." : "Send Reset Link"}
              </Button>
              <Button
                variant="ghost"
                onClick={() => setShowPasswordReset(false)}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
